export interface FileExplanation {
  path: string
  description: string
  purpose: string
  keyFeatures?: string[]
  relatedFiles?: string[]
}

export const mongoDBFileExplanations: Record<string, FileExplanation> = {
  // Root files
  "env.sample": {
    path: "env.sample",
    description: "Environment variables template file",
    purpose: "Provides a template for all required environment variables. Copy this to .env and fill in your values.",
    keyFeatures: [
      "MongoDB connection string",
      "JWT secrets configuration",
      "OAuth credentials",
      "Application settings",
    ],
  },
  "package.json": {
    path: "package.json",
    description: "NPM package configuration",
    purpose: "Defines project dependencies, scripts, and metadata.",
    keyFeatures: [
      "NestJS 11 dependencies",
      "Mongoose ODM",
      "Passport authentication",
      "Development and build scripts",
    ],
  },
  
  // Config files
  "src/config/app.config.ts": {
    path: "src/config/app.config.ts",
    description: "Application configuration",
    purpose: "Centralizes all application settings with type safety using ConfigService.",
    keyFeatures: [
      "JWT configuration",
      "OAuth settings",
      "Port and environment",
      "Security settings (bcrypt rounds)",
    ],
    relatedFiles: ["src/config/database.config.ts"],
  },
  "src/config/database.config.ts": {
    path: "src/config/database.config.ts",
    description: "MongoDB database configuration",
    purpose: "Configures MongoDB connection using Mongoose.",
    keyFeatures: [
      "MongoDB URI connection",
      "Connection options",
      "Type-safe configuration",
    ],
    relatedFiles: ["src/config/app.config.ts"],
  },
  
  // App files
  "src/app.module.ts": {
    path: "src/app.module.ts",
    description: "Root application module",
    purpose: "Orchestrates all feature modules and sets up global configuration, guards, and database connection.",
    keyFeatures: [
      "Imports all feature modules",
      "Configures Mongoose connection",
      "Sets up global guards (JWT, Roles)",
      "Initializes ConfigModule",
    ],
    relatedFiles: ["src/main.ts", "src/auth/auth.module.ts", "src/user/user.module.ts"],
  },
  "src/main.ts": {
    path: "src/main.ts",
    description: "Application entry point",
    purpose: "Bootstraps the NestJS application, sets up global pipes, CORS, and API documentation.",
    keyFeatures: [
      "Configures ValidationPipe",
      "Enables CORS",
      "Sets up Swagger and Scalar docs",
      "Starts the server",
    ],
    relatedFiles: ["src/app.module.ts"],
  },
  
  // Auth module
  "src/auth/auth.module.ts": {
    path: "src/auth/auth.module.ts",
    description: "Authentication module",
    purpose: "Encapsulates all authentication-related functionality including JWT, OAuth, and strategies.",
    keyFeatures: [
      "JWT module configuration",
      "Passport strategies registration",
      "Auth services and controllers",
      "Mongoose schema imports",
    ],
    relatedFiles: ["src/auth/auth.controller.ts", "src/auth/services/auth.service.ts"],
  },
  "src/auth/auth.controller.ts": {
    path: "src/auth/auth.controller.ts",
    description: "Authentication API endpoints",
    purpose: "Handles HTTP requests for registration, login, token refresh, and logout.",
    keyFeatures: [
      "POST /auth/register",
      "POST /auth/login",
      "POST /auth/refresh",
      "POST /auth/logout",
      "GET /auth/me",
    ],
    relatedFiles: ["src/auth/services/auth.service.ts", "src/auth/dto/"],
  },
  "src/auth/schemas/user.schema.ts": {
    path: "src/auth/schemas/user.schema.ts",
    description: "User Mongoose schema",
    purpose: "Defines the User document structure for MongoDB with validation and decorators.",
    keyFeatures: [
      "User fields (email, password, role, etc.)",
      "Timestamps (createdAt, updatedAt)",
      "Email uniqueness constraint",
      "Role enum validation",
    ],
    relatedFiles: ["src/auth/schemas/refresh-token.schema.ts"],
  },
  "src/auth/schemas/refresh-token.schema.ts": {
    path: "src/auth/schemas/refresh-token.schema.ts",
    description: "RefreshToken Mongoose schema",
    purpose: "Stores refresh tokens with device tracking and expiration management.",
    keyFeatures: [
      "Token storage",
      "Expiration tracking",
      "Revocation flag",
      "Device info (userAgent, IP)",
    ],
    relatedFiles: ["src/auth/schemas/user.schema.ts"],
  },
  "src/auth/guards/jwt-auth.guard.ts": {
    path: "src/auth/guards/jwt-auth.guard.ts",
    description: "JWT authentication guard",
    purpose: "Protects routes by validating JWT tokens. Allows @Public() decorated routes to skip authentication.",
    keyFeatures: [
      "Extends Passport JwtStrategy",
      "Checks for @Public decorator",
      "Applied globally in app.module.ts",
    ],
    relatedFiles: ["src/auth/strategies/jwt.strategy.ts", "src/auth/decorators/public.decorator.ts"],
  },
  "src/auth/strategies/jwt.strategy.ts": {
    path: "src/auth/strategies/jwt.strategy.ts",
    description: "JWT Passport strategy",
    purpose: "Validates JWT tokens and loads user data from the database.",
    keyFeatures: [
      "Extracts JWT from Authorization header",
      "Verifies token signature",
      "Loads user from database",
      "Attaches user to request object",
    ],
    relatedFiles: ["src/auth/guards/jwt-auth.guard.ts"],
  },
  "src/auth/strategies/google.strategy.ts": {
    path: "src/auth/strategies/google.strategy.ts",
    description: "Google OAuth Passport strategy",
    purpose: "Handles Google OAuth 2.0 authentication flow.",
    keyFeatures: [
      "OAuth configuration",
      "Profile validation",
      "User creation/linking",
      "Returns user data",
    ],
    relatedFiles: ["src/auth/oauth/oauth.controller.ts"],
  },
  "src/auth/decorators/public.decorator.ts": {
    path: "src/auth/decorators/public.decorator.ts",
    description: "Public route decorator",
    purpose: "Marks routes that should skip JWT authentication.",
    keyFeatures: [
      "Custom decorator",
      "Uses SetMetadata",
      "Checked by JwtAuthGuard",
    ],
    relatedFiles: ["src/auth/guards/jwt-auth.guard.ts"],
  },
  "src/auth/decorators/current-user.decorator.ts": {
    path: "src/auth/decorators/current-user.decorator.ts",
    description: "Current user decorator",
    purpose: "Extracts the authenticated user from the request object.",
    keyFeatures: [
      "Parameter decorator",
      "Type-safe user extraction",
      "Used in controllers",
    ],
    relatedFiles: ["src/auth/guards/jwt-auth.guard.ts"],
  },
  "src/auth/decorators/index.ts": {
    path: "src/auth/decorators/index.ts",
    description: "Barrel export file for auth decorators",
    purpose: "Centralizes exports for cleaner imports throughout the application.",
    keyFeatures: [
      "Exports @Public decorator",
      "Exports @CurrentUser decorator",
      "Enables clean imports",
    ],
  },
  
  // Auth DTOs
  "src/auth/dto/register.dto.ts": {
    path: "src/auth/dto/register.dto.ts",
    description: "User registration data transfer object",
    purpose: "Validates and transforms user registration input with class-validator decorators.",
    keyFeatures: [
      "Email validation",
      "Password complexity requirements",
      "Name fields validation",
      "Automatic whitespace trimming",
    ],
    relatedFiles: ["src/auth/auth.controller.ts"],
  },
  "src/auth/dto/login.dto.ts": {
    path: "src/auth/dto/login.dto.ts",
    description: "User login data transfer object",
    purpose: "Validates email and password for login requests.",
    keyFeatures: [
      "Email format validation",
      "Required field validation",
      "API documentation decorators",
    ],
    relatedFiles: ["src/auth/auth.controller.ts"],
  },
  "src/auth/dto/refresh-token.dto.ts": {
    path: "src/auth/dto/refresh-token.dto.ts",
    description: "Token refresh request DTO",
    purpose: "Validates refresh token input for token refresh operations.",
    keyFeatures: [
      "Refresh token validation",
      "String type checking",
    ],
    relatedFiles: ["src/auth/auth.controller.ts"],
  },
  "src/auth/dto/auth-response.dto.ts": {
    path: "src/auth/dto/auth-response.dto.ts",
    description: "Authentication response DTO",
    purpose: "Defines the structure of authentication responses (login, register, refresh).",
    keyFeatures: [
      "Access token field",
      "Refresh token field",
      "User data field",
      "Swagger documentation",
    ],
    relatedFiles: ["src/auth/services/auth.service.ts"],
  },
  "src/auth/dto/index.ts": {
    path: "src/auth/dto/index.ts",
    description: "Barrel export for auth DTOs",
    purpose: "Exports all authentication data transfer objects for clean imports.",
    keyFeatures: [
      "Exports RegisterDto",
      "Exports LoginDto",
      "Exports RefreshTokenDto",
      "Exports AuthResponseDto",
    ],
  },
  
  // Auth guards
  "src/auth/guards/index.ts": {
    path: "src/auth/guards/index.ts",
    description: "Barrel export for auth guards",
    purpose: "Exports authentication guards for use in controllers and app module.",
    keyFeatures: [
      "Exports JwtAuthGuard",
    ],
  },
  
  // OAuth
  "src/auth/oauth/oauth.controller.ts": {
    path: "src/auth/oauth/oauth.controller.ts",
    description: "OAuth authentication controller",
    purpose: "Handles OAuth flows for external providers (Google, etc.).",
    keyFeatures: [
      "Google OAuth endpoints",
      "Callback handling",
      "Token generation after OAuth",
      "Frontend redirect with tokens",
    ],
    relatedFiles: ["src/auth/strategies/google.strategy.ts"],
  },
  "src/auth/oauth/index.ts": {
    path: "src/auth/oauth/index.ts",
    description: "Barrel export for OAuth controllers",
    purpose: "Exports OAuth-related controllers.",
    keyFeatures: [
      "Exports OAuthController",
    ],
  },
  
  // Schemas
  "src/auth/schemas/index.ts": {
    path: "src/auth/schemas/index.ts",
    description: "Barrel export for auth schemas",
    purpose: "Exports Mongoose schemas for authentication.",
    keyFeatures: [
      "Exports RefreshToken schema",
    ],
  },
  
  // Strategies
  "src/auth/strategies/index.ts": {
    path: "src/auth/strategies/index.ts",
    description: "Barrel export for Passport strategies",
    purpose: "Exports all Passport authentication strategies.",
    keyFeatures: [
      "Exports JwtStrategy",
      "Exports GoogleStrategy",
    ],
  },
  
  // Shared module
  "src/shared/shared.module.ts": {
    path: "src/shared/shared.module.ts",
    description: "Shared utilities module",
    purpose: "Provides common services, filters, and interceptors used across the application.",
    keyFeatures: [
      "TypedConfigService",
      "LoggerService",
      "Global exports",
    ],
    relatedFiles: ["src/shared/services/typed-config.service.ts"],
  },
  "src/shared/index.ts": {
    path: "src/shared/index.ts",
    description: "Barrel export for shared module",
    purpose: "Centralizes all shared exports for easy importing across the application.",
    keyFeatures: [
      "Exports all shared utilities",
      "Exports DTOs, enums, guards",
      "Single import source",
    ],
  },
  
  // Shared decorators
  "src/shared/decorators/roles.decorator.ts": {
    path: "src/shared/decorators/roles.decorator.ts",
    description: "Roles decorator",
    purpose: "Marks routes with required roles for access control.",
    keyFeatures: [
      "Custom decorator",
      "Accepts multiple roles",
      "Used with RolesGuard",
    ],
    relatedFiles: ["src/shared/guards/roles.guard.ts"],
  },
  "src/shared/decorators/index.ts": {
    path: "src/shared/decorators/index.ts",
    description: "Barrel export for shared decorators",
    purpose: "Exports shared decorator functions.",
    keyFeatures: [
      "Exports @Roles decorator",
    ],
  },
  
  // Shared DTOs
  "src/shared/dto/global-response.dto.ts": {
    path: "src/shared/dto/global-response.dto.ts",
    description: "Global API response wrapper",
    purpose: "Provides consistent response format for all API endpoints.",
    keyFeatures: [
      "Message field",
      "Status code",
      "Data payload",
      "Error details",
    ],
    relatedFiles: ["src/shared/interceptors/response-transform.interceptor.ts"],
  },
  "src/shared/dto/pagination-query.dto.ts": {
    path: "src/shared/dto/pagination-query.dto.ts",
    description: "Pagination query parameters DTO",
    purpose: "Validates pagination query parameters (page, limit, sort, order).",
    keyFeatures: [
      "Page number validation",
      "Limit validation",
      "Sort field validation",
      "Order direction validation",
    ],
  },
  "src/shared/dto/pagination-response.dto.ts": {
    path: "src/shared/dto/pagination-response.dto.ts",
    description: "Pagination response wrapper",
    purpose: "Wraps paginated data with metadata (total, page, limit, etc.).",
    keyFeatures: [
      "Data array",
      "Total count",
      "Current page",
      "Total pages",
    ],
  },
  "src/shared/dto/index.ts": {
    path: "src/shared/dto/index.ts",
    description: "Barrel export for shared DTOs",
    purpose: "Exports all shared data transfer objects.",
    keyFeatures: [
      "Exports GlobalResponseDto",
      "Exports pagination DTOs",
    ],
  },
  
  // Shared enums
  "src/shared/enums/user-role.enum.ts": {
    path: "src/shared/enums/user-role.enum.ts",
    description: "User role enumeration",
    purpose: "Defines available user roles for RBAC (Role-Based Access Control).",
    keyFeatures: [
      "USER role",
      "ADMIN role",
      "SUPER_ADMIN role",
    ],
    relatedFiles: ["src/shared/guards/roles.guard.ts", "src/shared/decorators/roles.decorator.ts"],
  },
  "src/shared/enums/environment.enum.ts": {
    path: "src/shared/enums/environment.enum.ts",
    description: "Environment enumeration",
    purpose: "Defines application environment types for configuration.",
    keyFeatures: [
      "DEVELOPMENT",
      "STAGING",
      "PRODUCTION",
    ],
  },
  "src/shared/enums/auth-provider.enum.ts": {
    path: "src/shared/enums/auth-provider.enum.ts",
    description: "Authentication provider enumeration",
    purpose: "Defines supported OAuth and authentication providers.",
    keyFeatures: [
      "EMAIL provider",
      "GOOGLE provider",
      "Extensible for more providers",
    ],
  },
  "src/shared/enums/config-keys.enum.ts": {
    path: "src/shared/enums/config-keys.enum.ts",
    description: "Configuration key enumerations",
    purpose: "Provides type-safe keys for configuration access, eliminating hardcoded strings.",
    keyFeatures: [
      "ConfigKey enum",
      "EnvKey enum",
      "Type safety",
      "IDE autocomplete",
    ],
    relatedFiles: ["src/shared/services/typed-config.service.ts"],
  },
  "src/shared/enums/index.ts": {
    path: "src/shared/enums/index.ts",
    description: "Barrel export for shared enums",
    purpose: "Exports all enumeration types.",
    keyFeatures: [
      "Exports UserRole",
      "Exports Environment",
      "Exports AuthProvider",
      "Exports config enums",
    ],
  },
  
  // Shared filters
  "src/shared/filters/http-exception.filter.ts": {
    path: "src/shared/filters/http-exception.filter.ts",
    description: "Global exception filter",
    purpose: "Catches all HTTP exceptions and formats them consistently.",
    keyFeatures: [
      "Exception catching",
      "Error formatting",
      "Logging integration",
    ],
    relatedFiles: ["src/shared/dto/global-response.dto.ts"],
  },
  "src/shared/filters/index.ts": {
    path: "src/shared/filters/index.ts",
    description: "Barrel export for exception filters",
    purpose: "Exports exception filter classes.",
    keyFeatures: [
      "Exports HttpExceptionFilter",
    ],
  },
  
  // Shared guards
  "src/shared/guards/roles.guard.ts": {
    path: "src/shared/guards/roles.guard.ts",
    description: "Role-based authorization guard",
    purpose: "Checks if the authenticated user has the required role(s) for a route.",
    keyFeatures: [
      "Reads @Roles decorator metadata",
      "Compares user role",
      "Applied globally after JwtAuthGuard",
    ],
    relatedFiles: ["src/shared/decorators/roles.decorator.ts", "src/shared/enums/user-role.enum.ts"],
  },
  "src/shared/guards/index.ts": {
    path: "src/shared/guards/index.ts",
    description: "Barrel export for guards",
    purpose: "Exports authorization guard classes.",
    keyFeatures: [
      "Exports RolesGuard",
    ],
  },
  
  // Shared interceptors
  "src/shared/interceptors/response-transform.interceptor.ts": {
    path: "src/shared/interceptors/response-transform.interceptor.ts",
    description: "Response transformation interceptor",
    purpose: "Automatically wraps all controller responses in GlobalResponseDto format.",
    keyFeatures: [
      "Global interceptor",
      "Consistent formatting",
      "Success message generation",
    ],
    relatedFiles: ["src/shared/dto/global-response.dto.ts"],
  },
  "src/shared/interceptors/index.ts": {
    path: "src/shared/interceptors/index.ts",
    description: "Barrel export for interceptors",
    purpose: "Exports interceptor classes.",
    keyFeatures: [
      "Exports ResponseTransformInterceptor",
    ],
  },
  
  // Shared services
  "src/shared/services/typed-config.service.ts": {
    path: "src/shared/services/typed-config.service.ts",
    description: "Type-safe configuration service",
    purpose: "Provides helper methods for accessing configuration with type safety and validation.",
    keyFeatures: [
      "Type-safe config access",
      "Helper methods",
      "Validation",
      "IDE autocomplete",
    ],
    relatedFiles: ["src/shared/enums/config-keys.enum.ts"],
  },
  "src/shared/services/index.ts": {
    path: "src/shared/services/index.ts",
    description: "Barrel export for shared services",
    purpose: "Exports shared service classes.",
    keyFeatures: [
      "Exports TypedConfigService",
    ],
  },
  
  // Shared types
  "src/shared/types/config.types.ts": {
    path: "src/shared/types/config.types.ts",
    description: "Configuration TypeScript types",
    purpose: "Defines TypeScript interfaces for configuration objects.",
    keyFeatures: [
      "AppConfig interface",
      "DatabaseConfig interface",
      "Type safety",
    ],
  },
  "src/shared/types/index.ts": {
    path: "src/shared/types/index.ts",
    description: "Barrel export for TypeScript types",
    purpose: "Exports type definitions.",
    keyFeatures: [
      "Exports config types",
    ],
  },
  
  // Shared utils
  "src/shared/utils/password.util.ts": {
    path: "src/shared/utils/password.util.ts",
    description: "Password utility functions",
    purpose: "Provides helper functions for password hashing and comparison.",
    keyFeatures: [
      "bcrypt hashing",
      "Password comparison",
      "Salt rounds configuration",
    ],
  },
  "src/shared/utils/environment.util.ts": {
    path: "src/shared/utils/environment.util.ts",
    description: "Environment utility functions",
    purpose: "Helper functions for checking current environment.",
    keyFeatures: [
      "isDevelopment check",
      "isProduction check",
      "isStaging check",
    ],
  },
  "src/shared/utils/index.ts": {
    path: "src/shared/utils/index.ts",
    description: "Barrel export for utility functions",
    purpose: "Exports utility helper functions.",
    keyFeatures: [
      "Exports password utils",
      "Exports environment utils",
    ],
  },
  
  // User module
  "src/user/user.module.ts": {
    path: "src/user/user.module.ts",
    description: "User management module",
    purpose: "Encapsulates user-related functionality including profile management and admin operations.",
    keyFeatures: [
      "User service and controller",
      "Mongoose schema imports",
      "Module configuration",
    ],
    relatedFiles: ["src/user/user.controller.ts", "src/user/user.service.ts"],
  },
  "src/user/user.controller.ts": {
    path: "src/user/user.controller.ts",
    description: "User API endpoints",
    purpose: "Handles HTTP requests for user profile and admin operations.",
    keyFeatures: [
      "GET /users/profile",
      "PUT /users/profile",
      "PUT /users/change-password",
      "GET /users (Admin)",
      "DELETE /users/:id (Super Admin)",
    ],
    relatedFiles: ["src/user/user.service.ts", "src/user/dto/"],
  },
  "src/user/user.service.ts": {
    path: "src/user/user.service.ts",
    description: "User business logic",
    purpose: "Implements user-related operations including profile updates and password changes.",
    keyFeatures: [
      "Profile management",
      "Password hashing",
      "User queries",
      "Admin operations",
    ],
    relatedFiles: ["src/user/user.controller.ts"],
  },
  
  // User DTOs
  "src/user/dto/user-response.dto.ts": {
    path: "src/user/dto/user-response.dto.ts",
    description: "User response data transfer object",
    purpose: "Defines the structure of user data returned in API responses.",
    keyFeatures: [
      "User fields formatting",
      "Excludes sensitive data",
      "Swagger documentation",
    ],
    relatedFiles: ["src/user/user.controller.ts"],
  },
  "src/user/dto/update-user.dto.ts": {
    path: "src/user/dto/update-user.dto.ts",
    description: "Update user profile DTO",
    purpose: "Validates user profile update requests.",
    keyFeatures: [
      "Optional name fields",
      "Phone number validation",
      "Field validation",
    ],
    relatedFiles: ["src/user/user.controller.ts"],
  },
  "src/user/dto/change-password.dto.ts": {
    path: "src/user/dto/change-password.dto.ts",
    description: "Change password DTO",
    purpose: "Validates password change requests with current and new password.",
    keyFeatures: [
      "Current password validation",
      "New password complexity check",
      "Confirmation matching",
    ],
    relatedFiles: ["src/user/user.controller.ts"],
  },
  "src/user/dto/index.ts": {
    path: "src/user/dto/index.ts",
    description: "Barrel export for user DTOs",
    purpose: "Exports all user data transfer objects.",
    keyFeatures: [
      "Exports UserResponseDto",
      "Exports UpdateUserDto",
      "Exports ChangePasswordDto",
    ],
  },
  
  // User schemas
  "src/user/schemas/user.schema.ts": {
    path: "src/user/schemas/user.schema.ts",
    description: "User Mongoose schema",
    purpose: "Defines the User document structure for MongoDB with validation and decorators.",
    keyFeatures: [
      "User fields (email, password, role, etc.)",
      "Timestamps (createdAt, updatedAt)",
      "Email uniqueness constraint",
      "Role enum validation",
    ],
    relatedFiles: ["src/user/schemas/user-provider.schema.ts"],
  },
  "src/user/schemas/user-provider.schema.ts": {
    path: "src/user/schemas/user-provider.schema.ts",
    description: "UserProvider Mongoose schema",
    purpose: "Links users to OAuth providers and stores provider-specific data.",
    keyFeatures: [
      "Provider type (Google, GitHub, etc.)",
      "Provider user ID",
      "OAuth tokens storage",
      "User reference",
    ],
    relatedFiles: ["src/user/schemas/user.schema.ts"],
  },
  "src/user/schemas/index.ts": {
    path: "src/user/schemas/index.ts",
    description: "Barrel export for user schemas",
    purpose: "Exports user Mongoose schemas.",
    keyFeatures: [
      "Exports User schema",
      "Exports UserProvider schema",
    ],
  },
  
  // Root app files
  "src/app.controller.ts": {
    path: "src/app.controller.ts",
    description: "Root application controller",
    purpose: "Handles root-level HTTP requests like health checks.",
    keyFeatures: [
      "Health check endpoint",
      "App info endpoint",
      "Public routes",
    ],
    relatedFiles: ["src/app.service.ts"],
  },
  "src/app.service.ts": {
    path: "src/app.service.ts",
    description: "Root application service",
    purpose: "Provides business logic for root-level operations.",
    keyFeatures: [
      "Health check logic",
      "App information",
    ],
    relatedFiles: ["src/app.controller.ts"],
  },
  
  // Build config files
  "tsconfig.json": {
    path: "tsconfig.json",
    description: "TypeScript configuration",
    purpose: "Configures TypeScript compiler options for the project.",
    keyFeatures: [
      "Strict mode enabled",
      "Decorators enabled",
      "Path aliases configured",
      "Target ES2021",
    ],
  },
  "tsconfig.build.json": {
    path: "tsconfig.build.json",
    description: "TypeScript build configuration",
    purpose: "Extends tsconfig.json with build-specific settings.",
    keyFeatures: [
      "Excludes test files",
      "Excludes node_modules",
      "Production build settings",
    ],
  },
  "nest-cli.json": {
    path: "nest-cli.json",
    description: "Nest CLI configuration",
    purpose: "Configures Nest CLI behavior and code generation.",
    keyFeatures: [
      "Source root configuration",
      "Compiler options",
      "Asset watching",
    ],
  },
  "README.md": {
    path: "README.md",
    description: "Project documentation",
    purpose: "Main documentation file with setup instructions and feature overview.",
    keyFeatures: [
      "Quick start guide",
      "Feature list",
      "API endpoints",
      "Configuration instructions",
    ],
  },
  
  // Scripts
  "scripts/setup.sh": {
    path: "scripts/setup.sh",
    description: "Interactive setup script (Linux/Mac)",
    purpose: "Guides users through initial setup: dependencies, environment configuration, and JWT secrets.",
    keyFeatures: [
      "npm install",
      "Environment setup",
      "JWT secret generation",
      "MongoDB connection validation",
    ],
  },
  "scripts/setup.bat": {
    path: "scripts/setup.bat",
    description: "Interactive setup script (Windows)",
    purpose: "Windows version of the setup script for initial project configuration.",
    keyFeatures: [
      "npm install",
      "Environment file creation",
      "JWT secrets generation",
      "Windows compatibility",
    ],
  },
}

export const mySQLFileExplanations: Record<string, FileExplanation> = {
  // Add migration-specific explanations
  "src/migrations/1735000000000-InitialSchema.ts": {
    path: "src/migrations/1735000000000-InitialSchema.ts",
    description: "Initial database schema migration",
    purpose: "Creates the initial database tables (users, refresh_tokens, user_providers) with proper indexes and foreign keys.",
    keyFeatures: [
      "Creates users table",
      "Creates refresh_tokens table",
      "Creates user_providers table",
      "Adds foreign key constraints",
      "Includes up and down methods",
    ],
    relatedFiles: ["src/config/typeorm.config.ts"],
  },
  "src/config/typeorm.config.ts": {
    path: "src/config/typeorm.config.ts",
    description: "TypeORM CLI configuration",
    purpose: "Configures TypeORM for migration generation and execution via CLI commands.",
    keyFeatures: [
      "Database connection settings",
      "Migration paths",
      "Entity paths",
      "CLI configuration",
    ],
    relatedFiles: ["src/config/database.config.ts"],
  },
  "src/auth/entities/user.entity.ts": {
    path: "src/auth/entities/user.entity.ts",
    description: "User TypeORM entity",
    purpose: "Defines the User table structure with decorators, relationships, and constraints.",
    keyFeatures: [
      "UUID primary key",
      "Email unique constraint and index",
      "Role enum column",
      "Timestamps (createdAt, updatedAt)",
      "Relationships to RefreshToken and UserProvider",
    ],
    relatedFiles: ["src/auth/entities/refresh-token.entity.ts"],
  },
  "src/auth/entities/refresh-token.entity.ts": {
    path: "src/auth/entities/refresh-token.entity.ts",
    description: "RefreshToken TypeORM entity",
    purpose: "Stores refresh tokens with device tracking, expiration, and revocation support.",
    keyFeatures: [
      "UUID primary key",
      "Foreign key to User",
      "Cascade delete",
      "Device tracking fields",
      "Expiration and revocation",
    ],
    relatedFiles: ["src/auth/entities/user.entity.ts"],
  },
  "scripts/migrations.sh": {
    path: "scripts/migrations.sh",
    description: "Migration management script",
    purpose: "Helper script for running, reverting, and generating migrations.",
    keyFeatures: [
      "Run migrations",
      "Revert migrations",
      "Generate from entities",
      "Create empty migrations",
    ],
  },
  
  // Include all MongoDB explanations plus MySQL-specific ones
  ...mongoDBFileExplanations,
}

export const reactShadcnFileExplanations: Record<string, FileExplanation> = {
  // UI Components
  "src/components/ui/button.tsx": {
    path: "src/components/ui/button.tsx",
    description: "Shadcn UI Button component",
    purpose: "Accessible, customizable button with multiple variants (default, destructive, outline, secondary, ghost, link).",
    keyFeatures: [
      "Built on Radix UI primitives",
      "Multiple size options (sm, md, lg)",
      "Disabled state support",
      "Icon support",
      "Fully accessible (ARIA)",
    ],
  },
  "src/components/ui/card.tsx": {
    path: "src/components/ui/card.tsx",
    description: "Shadcn UI Card component",
    purpose: "Container component with header, content, and footer subcomponents for structured content display.",
    keyFeatures: [
      "Responsive design",
      "Composable subcomponents",
      "Theme-aware styling",
    ],
  },
  
  // Common Components
  "src/components/common/ErrorBoundary.tsx": {
    path: "src/components/common/ErrorBoundary.tsx",
    description: "React Error Boundary component",
    purpose: "Catches JavaScript errors anywhere in the child component tree and displays a fallback UI.",
    keyFeatures: [
      "Error catching and logging",
      "Fallback UI with retry button",
      "Development vs production messages",
      "Prevents app crashes",
    ],
  },
  "src/components/common/EmojiPicker.tsx": {
    path: "src/components/common/EmojiPicker.tsx",
    description: "Emoji picker component",
    purpose: "Provides emoji selection interface using emoji-mart library with theme support.",
    keyFeatures: [
      "Integration with emoji-mart",
      "Theme-aware (light/dark)",
      "Callback on emoji selection",
      "Customizable positioning",
    ],
  },
  "src/components/common/Modal.tsx": {
    path: "src/components/common/Modal.tsx",
    description: "Modal dialog component",
    purpose: "Reusable modal with backdrop, close button, and customizable content.",
    keyFeatures: [
      "Click outside to close",
      "ESC key support",
      "Multiple size options",
      "Smooth animations",
      "Portal rendering",
    ],
  },
  "src/components/common/ConfirmationDialog.tsx": {
    path: "src/components/common/ConfirmationDialog.tsx",
    description: "Confirmation dialog component",
    purpose: "Modal for confirming destructive or important actions before execution.",
    keyFeatures: [
      "Confirm/Cancel buttons",
      "Customizable messages",
      "Variant support (danger, warning, info)",
      "Keyboard shortcuts",
    ],
  },
  "src/components/common/GlobalLoader.tsx": {
    path: "src/components/common/GlobalLoader.tsx",
    description: "Global loading indicator",
    purpose: "Displays loading state with optional message, managed by Zustand store.",
    keyFeatures: [
      "Centralized state management",
      "Nested loading support",
      "Custom loading messages",
      "Full-screen overlay",
    ],
  },
  
  // Layout Components
  "src/components/layout/Sidebar.tsx": {
    path: "src/components/layout/Sidebar.tsx",
    description: "Resizable application sidebar",
    purpose: "Navigation sidebar with drag handle for width adjustment and persistent state.",
    keyFeatures: [
      "Drag-to-resize functionality",
      "Width persistence (localStorage)",
      "Active route highlighting",
      "Mobile responsive",
      "Smooth animations",
    ],
  },
  "src/components/layout/Header.tsx": {
    path: "src/components/layout/Header.tsx",
    description: "Application header",
    purpose: "Top navigation bar with search, notifications, theme toggle, and user menu.",
    keyFeatures: [
      "Search input",
      "Notification badge",
      "Theme switcher",
      "User profile dropdown",
      "Responsive design",
    ],
  },
  
  // Dashboard Components
  "src/components/dashboard/MetricCard.tsx": {
    path: "src/components/dashboard/MetricCard.tsx",
    description: "Dashboard metric card",
    purpose: "Displays key metrics with value, trend indicator, and percentage change.",
    keyFeatures: [
      "Trend arrows (up/down)",
      "Color-coded indicators",
      "Icon support",
      "Percentage change display",
    ],
  },
  "src/components/dashboard/DataTable.tsx": {
    path: "src/components/dashboard/DataTable.tsx",
    description: "Sortable data table",
    purpose: "Table component with search, sorting, and pagination for data display.",
    keyFeatures: [
      "Column sorting",
      "Global search",
      "Pagination",
      "Row actions",
      "Empty state handling",
    ],
  },
  "src/components/dashboard/ChartCard.tsx": {
    path: "src/components/dashboard/ChartCard.tsx",
    description: "Chart display card",
    purpose: "Wrapper for charts (bar, line) with title and description.",
    keyFeatures: [
      "Bar chart support",
      "Line chart support",
      "Responsive sizing",
      "Color customization",
    ],
  },
  "src/components/dashboard/ActivityFeed.tsx": {
    path: "src/components/dashboard/ActivityFeed.tsx",
    description: "Activity timeline feed",
    purpose: "Displays chronological list of activities with icons and timestamps.",
    keyFeatures: [
      "Timeline visualization",
      "Icon support",
      "Relative timestamps",
      "Scrollable container",
    ],
  },
  
  // Chat Components
  "src/components/chat/ChatList.tsx": {
    path: "src/components/chat/ChatList.tsx",
    description: "Chat conversations list",
    purpose: "Displays list of conversations with unread badges, online status, and search.",
    keyFeatures: [
      "Conversation search",
      "Unread message badges",
      "Online status indicators",
      "Last message preview",
      "Active conversation highlight",
    ],
  },
  "src/components/chat/ChatMessage.tsx": {
    path: "src/components/chat/ChatMessage.tsx",
    description: "Chat message component",
    purpose: "Renders individual chat messages with reactions, attachments, and actions.",
    keyFeatures: [
      "Message reactions",
      "Pin/unpin functionality",
      "File attachments display",
      "Timestamp display",
      "Read receipts",
    ],
  },
  "src/components/chat/ChatInput.tsx": {
    path: "src/components/chat/ChatInput.tsx",
    description: "Chat message input",
    purpose: "Input area for composing messages with emoji picker and file upload.",
    keyFeatures: [
      "Emoji picker integration",
      "File attachment",
      "Send on Enter",
      "Multiline support",
      "Character counter",
    ],
  },
  
  // Pages
  "src/pages/DashboardPage.tsx": {
    path: "src/pages/DashboardPage.tsx",
    description: "Dashboard page (smart component)",
    purpose: "Container page that handles data fetching and state for the dashboard view.",
    keyFeatures: [
      "Data fetching logic",
      "State management",
      "Error handling",
      "Loading states",
    ],
    relatedFiles: ["src/views/DashboardView.tsx"],
  },
  "src/pages/ChatPage.tsx": {
    path: "src/pages/ChatPage.tsx",
    description: "Chat page (smart component)",
    purpose: "Container page that manages chat state, fetches conversations, and handles messaging logic.",
    keyFeatures: [
      "Conversation management",
      "Message handling",
      "Real-time updates (mock)",
      "User search",
    ],
    relatedFiles: ["src/views/ChatView.tsx"],
  },
  "src/pages/LoginPage.tsx": {
    path: "src/pages/LoginPage.tsx",
    description: "Login page",
    purpose: "Authentication page with login form and validation.",
    keyFeatures: [
      "Email/password validation",
      "Form error handling",
      "OAuth integration (Google)",
      "Remember me checkbox",
    ],
  },
  
  // Views
  "src/views/DashboardView.tsx": {
    path: "src/views/DashboardView.tsx",
    description: "Dashboard view (presentational)",
    purpose: "Pure presentational component that renders dashboard UI based on props.",
    keyFeatures: [
      "Metric cards",
      "Charts display",
      "Activity feed",
      "Quick actions",
    ],
    relatedFiles: ["src/pages/DashboardPage.tsx"],
  },
  "src/views/ChatView.tsx": {
    path: "src/views/ChatView.tsx",
    description: "Chat view (presentational)",
    purpose: "Pure presentational component that renders chat interface based on props.",
    keyFeatures: [
      "Two-column layout",
      "Conversation list",
      "Message thread",
      "Input area",
    ],
    relatedFiles: ["src/pages/ChatPage.tsx"],
  },
  
  // Layouts
  "src/layouts/AppLayout.tsx": {
    path: "src/layouts/AppLayout.tsx",
    description: "Main application layout",
    purpose: "Wrapper layout for authenticated pages with sidebar and header.",
    keyFeatures: [
      "Sidebar integration",
      "Header integration",
      "Content area",
      "Responsive design",
    ],
  },
  "src/layouts/AuthLayout.tsx": {
    path: "src/layouts/AuthLayout.tsx",
    description: "Authentication layout",
    purpose: "Layout wrapper for login/signup pages with centered form design.",
    keyFeatures: [
      "Centered content",
      "Background styling",
      "Logo display",
      "Responsive",
    ],
  },
  
  // Services
  "src/services/api/client.ts": {
    path: "src/services/api/client.ts",
    description: "Axios API client",
    purpose: "Configures Axios instance with base URL, interceptors, and error handling.",
    keyFeatures: [
      "Request interceptors (auth token)",
      "Response interceptors (error handling)",
      "Token refresh logic",
      "Retry mechanism",
    ],
  },
  "src/services/api/auth.service.ts": {
    path: "src/services/api/auth.service.ts",
    description: "Authentication API service",
    purpose: "Handles all authentication-related API calls (login, register, logout).",
    keyFeatures: [
      "Login method",
      "Register method",
      "Logout method",
      "Token refresh",
    ],
    relatedFiles: ["src/services/api/client.ts"],
  },
  "src/services/api/product.service.ts": {
    path: "src/services/api/product.service.ts",
    description: "Product API service",
    purpose: "Example CRUD service for products with full create, read, update, delete operations.",
    keyFeatures: [
      "getAll()",
      "getById()",
      "create()",
      "update()",
      "delete()",
    ],
  },
  
  // Store
  "src/store/authStore.ts": {
    path: "src/store/authStore.ts",
    description: "Authentication Zustand store",
    purpose: "Manages authentication state (user, token) with persistence.",
    keyFeatures: [
      "User state",
      "Token storage",
      "Login/logout actions",
      "Persist middleware",
    ],
  },
  "src/store/themeStore.ts": {
    path: "src/store/themeStore.ts",
    description: "Theme Zustand store",
    purpose: "Manages theme state (light/dark) with localStorage persistence.",
    keyFeatures: [
      "Theme toggle",
      "System preference detection",
      "Persistent storage",
      "Theme sync across tabs",
    ],
  },
  "src/store/sidebarStore.ts": {
    path: "src/store/sidebarStore.ts",
    description: "Sidebar Zustand store",
    purpose: "Manages sidebar state (width, collapsed) with persistence.",
    keyFeatures: [
      "Width state",
      "Collapse state",
      "Update methods",
      "LocalStorage sync",
    ],
  },
  "src/store/confirmationStore.ts": {
    path: "src/store/confirmationStore.ts",
    description: "Confirmation dialog Zustand store",
    purpose: "Manages global confirmation dialog state for programmatic control.",
    keyFeatures: [
      "Dialog visibility",
      "Message and title",
      "Confirm/cancel callbacks",
      "Promise-based API",
    ],
  },
  "src/store/loaderStore.ts": {
    path: "src/store/loaderStore.ts",
    description: "Global loader Zustand store",
    purpose: "Manages global loading state with support for nested loading operations.",
    keyFeatures: [
      "Loading counter",
      "Loading messages",
      "Show/hide methods",
      "Nested loading support",
    ],
  },
  
  // Hooks
  "src/hooks/useAuth.ts": {
    path: "src/hooks/useAuth.ts",
    description: "Authentication custom hook",
    purpose: "Provides authentication state and methods (login, logout, register).",
    keyFeatures: [
      "User state access",
      "Login/logout methods",
      "isAuthenticated boolean",
      "Auto token refresh",
    ],
    relatedFiles: ["src/store/authStore.ts"],
  },
  "src/hooks/useTheme.ts": {
    path: "src/hooks/useTheme.ts",
    description: "Theme custom hook",
    purpose: "Provides theme state and toggle functionality.",
    keyFeatures: [
      "Current theme",
      "Toggle theme",
      "System preference",
      "DOM class sync",
    ],
    relatedFiles: ["src/store/themeStore.ts"],
  },
  "src/hooks/useProducts.ts": {
    path: "src/hooks/useProducts.ts",
    description: "Products data hook",
    purpose: "Example custom hook for fetching and managing product data.",
    keyFeatures: [
      "Data fetching",
      "Loading state",
      "Error handling",
      "CRUD operations",
    ],
    relatedFiles: ["src/services/api/product.service.ts"],
  },
  
  // Types
  "src/types/auth.types.ts": {
    path: "src/types/auth.types.ts",
    description: "Authentication TypeScript types",
    purpose: "Defines interfaces for user, login, register, and auth responses.",
    keyFeatures: [
      "User interface",
      "LoginCredentials interface",
      "AuthResponse interface",
      "Type safety",
    ],
  },
  "src/types/chat.types.ts": {
    path: "src/types/chat.types.ts",
    description: "Chat TypeScript types",
    purpose: "Defines interfaces for conversations, messages, and chat-related data.",
    keyFeatures: [
      "Conversation interface",
      "Message interface",
      "Reaction interface",
      "Attachment interface",
    ],
  },
  
  // Root files
  "src/App.tsx": {
    path: "src/App.tsx",
    description: "Root application component",
    purpose: "Sets up routing, theme provider, and global components.",
    keyFeatures: [
      "React Router setup",
      "Theme provider",
      "Error boundary",
      "Global components",
    ],
  },
  "src/main.tsx": {
    path: "src/main.tsx",
    description: "Application entry point",
    purpose: "Renders the React app into the DOM.",
    keyFeatures: [
      "React DOM rendering",
      "Strict mode",
      "CSS imports",
    ],
  },
  "package.json": {
    path: "package.json",
    description: "NPM package configuration",
    purpose: "Defines project dependencies, scripts, and metadata.",
    keyFeatures: [
      "React 18",
      "Vite",
      "Shadcn UI / Tailwind CSS",
      "Zustand",
      "Dev scripts",
    ],
  },
}

export const nextShadcnFileExplanations: Record<string, FileExplanation> = {
  // App Router Files
  "src/app/layout.tsx": {
    path: "src/app/layout.tsx",
    description: "Root layout component",
    purpose: "Defines the root HTML structure, metadata, and global providers for the entire app.",
    keyFeatures: [
      "HTML and body tags",
      "Theme provider",
      "Font optimization",
      "Global styles",
      "Metadata configuration",
    ],
  },
  "src/app/page.tsx": {
    path: "src/app/page.tsx",
    description: "Home page",
    purpose: "Landing page component for the root route (/)",
    keyFeatures: [
      "Server component by default",
      "SEO metadata",
      "Static or dynamic rendering",
    ],
  },
  "src/app/error.tsx": {
    path: "src/app/error.tsx",
    description: "Error boundary page",
    purpose: "Catches errors in the app and displays a fallback UI with retry option.",
    keyFeatures: [
      "'use client' directive",
      "Error catching",
      "Reset functionality",
      "User-friendly error message",
    ],
  },
  "src/app/not-found.tsx": {
    path: "src/app/not-found.tsx",
    description: "404 Not Found page",
    purpose: "Custom 404 page displayed when no route matches.",
    keyFeatures: [
      "Custom design",
      "Navigation back home",
      "SEO metadata",
    ],
  },
  "src/app/dashboard/page.tsx": {
    path: "src/app/dashboard/page.tsx",
    description: "Dashboard page",
    purpose: "Main dashboard page displaying metrics, charts, and activities.",
    keyFeatures: [
      "Server component option",
      "Data fetching",
      "Dashboard view integration",
      "Metadata",
    ],
  },
  "src/app/chat/page.tsx": {
    path: "src/app/chat/page.tsx",
    description: "Chat page",
    purpose: "Chat application page with conversations and messaging.",
    keyFeatures: [
      "Client component",
      "Chat view integration",
      "Real-time updates (mock)",
    ],
  },
  "src/app/login/page.tsx": {
    path: "src/app/login/page.tsx",
    description: "Login page",
    purpose: "Authentication page with login form.",
    keyFeatures: [
      "Client component",
      "Form handling",
      "OAuth integration",
      "Validation",
    ],
  },
  
  // Components (UI)
  "src/components/ui/button.tsx": {
    path: "src/components/ui/button.tsx",
    description: "Shadcn UI Button component",
    purpose: "Accessible, customizable button with variants for Next.js.",
    keyFeatures: [
      "'use client' directive",
      "Multiple variants",
      "Size options",
      "Radix UI base",
    ],
  },
  
  // Components (Common)
  "src/components/common/ErrorBoundary.tsx": {
    path: "src/components/common/ErrorBoundary.tsx",
    description: "Error boundary component",
    purpose: "Client-side error boundary for Next.js applications.",
    keyFeatures: [
      "'use client' directive",
      "Error catching",
      "Fallback UI",
      "Works with App Router",
    ],
  },
  "src/components/common/EmojiPicker.tsx": {
    path: "src/components/common/EmojiPicker.tsx",
    description: "Emoji picker component",
    purpose: "Client-side emoji picker with theme support for Next.js.",
    keyFeatures: [
      "'use client' directive",
      "emoji-mart integration",
      "Theme-aware",
      "Callback support",
    ],
  },
  
  // Components (Layout)
  "src/components/layout/Sidebar.tsx": {
    path: "src/components/layout/Sidebar.tsx",
    description: "Resizable sidebar",
    purpose: "Navigation sidebar with drag-to-resize for Next.js.",
    keyFeatures: [
      "'use client' directive",
      "Width persistence",
      "Active route highlighting",
      "Responsive design",
    ],
  },
  "src/components/layout/Header.tsx": {
    path: "src/components/layout/Header.tsx",
    description: "Application header",
    purpose: "Top navigation bar for Next.js app.",
    keyFeatures: [
      "'use client' directive",
      "Search, notifications",
      "Theme toggle",
      "User menu",
    ],
  },
  "src/components/layout/AppLayout.tsx": {
    path: "src/components/layout/AppLayout.tsx",
    description: "App layout wrapper",
    purpose: "Layout wrapper with sidebar and header for authenticated pages.",
    keyFeatures: [
      "'use client' directive",
      "Layout composition",
      "Children rendering",
      "State management",
    ],
  },
  
  // Components (Dashboard)
  "src/components/dashboard/MetricCard.tsx": {
    path: "src/components/dashboard/MetricCard.tsx",
    description: "Dashboard metric card",
    purpose: "Displays metrics with trend indicators for Next.js.",
    keyFeatures: [
      "'use client' directive (if interactive)",
      "Trend visualization",
      "Icon support",
      "Responsive",
    ],
  },
  "src/components/dashboard/DataTable.tsx": {
    path: "src/components/dashboard/DataTable.tsx",
    description: "Sortable data table",
    purpose: "Client-side table with sorting and search for Next.js.",
    keyFeatures: [
      "'use client' directive",
      "Column sorting",
      "Search functionality",
      "Pagination",
    ],
  },
  
  // Components (Views)
  "src/components/views/DashboardView.tsx": {
    path: "src/components/views/DashboardView.tsx",
    description: "Dashboard presentational view",
    purpose: "Renders dashboard UI in Next.js app.",
    keyFeatures: [
      "Presentational component",
      "Metric cards",
      "Charts",
      "Activity feed",
    ],
  },
  "src/components/views/ChatView.tsx": {
    path: "src/components/views/ChatView.tsx",
    description: "Chat presentational view",
    purpose: "Renders chat interface in Next.js app.",
    keyFeatures: [
      "'use client' directive",
      "Two-column layout",
      "Conversation list",
      "Message display",
    ],
  },
  
  // Services
  "src/services/api/client.ts": {
    path: "src/services/api/client.ts",
    description: "Axios API client",
    purpose: "Configures API client for Next.js with interceptors.",
    keyFeatures: [
      "Request/response interceptors",
      "Token management",
      "Error handling",
      "Works client-side",
    ],
  },
  "src/services/api/auth.service.ts": {
    path: "src/services/api/auth.service.ts",
    description: "Authentication service",
    purpose: "Handles auth API calls in Next.js.",
    keyFeatures: [
      "Login/register methods",
      "Token refresh",
      "Client-side only",
    ],
  },
  
  // Store
  "src/store/authStore.ts": {
    path: "src/store/authStore.ts",
    description: "Authentication Zustand store",
    purpose: "Manages client-side auth state in Next.js.",
    keyFeatures: [
      "Client-side state",
      "Persist middleware",
      "User/token management",
    ],
  },
  "src/store/themeStore.ts": {
    path: "src/store/themeStore.ts",
    description: "Theme Zustand store",
    purpose: "Manages theme state in Next.js.",
    keyFeatures: [
      "Light/dark mode",
      "Persistent storage",
      "Client-side only",
    ],
  },
  
  // Types
  "src/types/auth.types.ts": {
    path: "src/types/auth.types.ts",
    description: "Authentication types",
    purpose: "TypeScript interfaces for auth in Next.js.",
    keyFeatures: [
      "User interface",
      "Auth response",
      "Type safety",
    ],
  },
  "src/types/chat.types.ts": {
    path: "src/types/chat.types.ts",
    description: "Chat types",
    purpose: "TypeScript interfaces for chat in Next.js.",
    keyFeatures: [
      "Conversation interface",
      "Message interface",
      "Type safety",
    ],
  },
  
  // Config Files
  "next.config.ts": {
    path: "next.config.ts",
    description: "Next.js configuration",
    purpose: "Configures Next.js build and runtime options.",
    keyFeatures: [
      "Image optimization",
      "Environment variables",
      "Redirects/rewrites",
      "Webpack config",
    ],
  },
  "package.json": {
    path: "package.json",
    description: "NPM package configuration",
    purpose: "Defines dependencies and scripts for Next.js app.",
    keyFeatures: [
      "Next.js 15",
      "React 19",
      "Shadcn UI / Tailwind",
      "Build scripts",
    ],
  },
  "tailwind.config.ts": {
    path: "tailwind.config.ts",
    description: "Tailwind CSS configuration",
    purpose: "Configures Tailwind CSS for Next.js.",
    keyFeatures: [
      "Theme customization",
      "Plugin configuration",
      "Content paths",
    ],
  },
}

